/* eslint-disable max-len */
/* eslint-disable import/order */
import {
  Flex,
  Menu,
  Tooltip,
} from 'antd';
import React, {
  useCallback, useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  Link, useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';

import { ListChecksIcon, SidebarSimple } from '@phosphor-icons/react';
import ButtonCustom from 'components/atoms/ButtonCustom';
import { getSetupGuideService } from 'services/onboarding';
import { Typography } from 'tfc-components';
import { ROUTE_PATHS } from 'utils/constants';
import mapModifiers from 'utils/functions';

// import { CaretLeftFilled, CaretRightFilled } from '@ant-design/icons';
// import { ChangePasswordModal, EditProfileModal, GenerateSecretTOtp } from './ProfileModals';
// import Icon from 'components/atoms/Icon';
// import { useAppDispatch, useAppSelector } from 'app/store';
// import { getCurrentLanguage, i18ChangeLanguage } from 'i18n';
// import { useTranslation } from 'react-i18next';
// import { useQueryClient } from 'react-query';
// import { logoutService } from 'services/authenticate';
// import { ROUTE_PATHS } from 'utils/constant';
// import { removeAccessToken, removeRefreshAccessToken } from 'services/common/storage';

const roles: Array<string> = [];
export interface MenuItem {
  key: string;
  icon?: {
    active: React.ReactNode;
    inactive: React.ReactNode
  };
  title?: string;
  path?: string;
  target?: string;
  items?: MenuItem[];
  role?: string;
  childrens?: string[];
  descriptions?: string;
  menu?: {
    key: string;
    path: string;
  }[]
}

export interface MenuItems {
  components?: string;
  items: MenuItem[];
}

interface SidebarProps {
  menuItems: MenuItems[] | MenuItem[];
  collapsed?: boolean;
  handleCollapsed?: () => void;
}

// const { Option } = Select;

const Sidebar: React.FC<SidebarProps> = ({
  menuItems, collapsed, handleCollapsed
}) => {
  // const dispatch = useAppDispatch();
  // const [isLoading, setLoading] = useState(false);
  // const profile = useAppSelector((state) => state.auth.profile);
  /* Hooks */
  // const navigate = useNavigate();

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const actionsRef = useRef<HTMLDivElement>(null);

  const [searchParams] = useSearchParams();
  // const keyExist = searchParams.get('key');

  /* States */
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const tabParam = searchParams.get('tab') || 'knowledge';
  const { data: setupGuideData } = useQuery({
    queryKey: ['setup-guide'],
    queryFn: getSetupGuideService,
  });

  const shouldShowMenuSetupGuide = !!setupGuideData?.toDos?.some((item) => !item.completed);
  const isActiveSetupGuide = location.pathname === ROUTE_PATHS.AGENT && searchParams.get('openSetupGuide') === 'true';
  // const featParams = searchParams.get('feat');

  /* Functions */
  // const handleLogout = async () => {
  //   try {
  //     setLoading(true);
  //     await logoutService();
  //     dispatch(resetAuth());
  //     navigate(ROUTE_PATHS.LOGIN);
  //   } catch {
  //     message.error('Logout failed!');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const handleEditProfile = () => { };

  const normalizeMenuItems = (input: MenuItems[] | MenuItem[]): MenuItems[] => {
    if (!Array.isArray(input)) return [];

    // If first element has "items" => already grouped
    const first = input[0] as any;
    if (first && Array.isArray(first.items)) return input as MenuItems[];

    // Otherwise it's a flat MenuItem[]
    return [{ items: input as MenuItem[] }];
  };

  const normalizedMenuItems = React.useMemo(() => normalizeMenuItems(menuItems), [menuItems]);

  const activeMenu = useCallback(() => {
    let found = false;

    normalizedMenuItems.every((menu) => menu.items.every((ele) => {
      if (
        tabParam.includes(ele.key) || ele.path === location.pathname
        || ele.childrens?.includes(location.pathname)
      ) {
        setSelectedKeys([ele.key]);
        found = true;
        return false; // stop
      }

      if (ele.items?.length) {
        return ele.items.every((item) => {
          if (
            item.path === location.pathname
            || item.childrens?.includes(location.pathname)
          ) {
            setSelectedKeys([item.key]);
            found = true;
            return false; // stop
          }
          return true;
        });
      }

      return true;
    }));

    if (!found) {
      setSelectedKeys([]);
    }
  }, [location.pathname, normalizedMenuItems, tabParam]);

  useEffect(() => {
    activeMenu();
  }, [activeMenu]);

  // const actionsMenu: MenuProps['items'] = [
  //   {
  //     label: (
  //       <span role="button" onClick={handleEditProfile}>Chỉnh sửa thông tin</span>
  //     ),
  //     key: '0'
  //   },
  //   {
  //     label: (
  //       <span role="button" onClick={handleLogout}>Đăng xuất</span>
  //     ),
  //     key: '1'
  //   }
  // ];

  const renderMenuSub = (menu: MenuItem): React.ReactNode => {
    if (!menu.items?.length) return null;

    // filter allowed items
    const visibleItems = menu.items.filter(
      (item) => !item.role || roles.includes(item.role)
    );

    if (!visibleItems.length) return null;

    return (
      <Menu.SubMenu
        className={mapModifiers('o-sidebar_submenu', selectedKeys.includes(menu.key) && 'active')}
        key={menu.key}
        title={menu.title ? t(menu.title) : ''}
        icon={selectedKeys.includes(menu.key)
          ? menu.icon?.active
          : menu.icon?.inactive}
      >
        {visibleItems.map((item) => (item.items?.length ? (
          renderMenuSub(item)
        ) : (
          <Menu.Item className={mapModifiers('o-sidebar_submenu_item', selectedKeys.includes(menu.key) && 'active')} key={item.key}>
            <Link className={mapModifiers('o-sidebar_submenu_link', selectedKeys.includes(menu.key) && 'active')} to={item.path || ''}>
              {item.icon
                && (selectedKeys.includes(item.key)
                  ? item.icon.active
                  : item.icon.inactive)}
              <span>{item.title ? t(item.title) : ''}</span>
            </Link>
          </Menu.Item>
        )))}
      </Menu.SubMenu>
    );
  };

  return (
    <div className="o-sidebar">
      <Flex className={mapModifiers('o-sidebar_logo', collapsed && 'collapsed')} align="center" justify={collapsed ? 'center' : 'space-between'}>
        <Typography.Text extendClasses={mapModifiers('fs-24x30 o-sidebar_logoText', !collapsed && 'collapsed')} fontweight="600">
          Ask2Auto
        </Typography.Text>
        <Tooltip className="o-sidebar_logo_icon" title={collapsed ? 'Expand' : ''} placement="right">
          <ButtonCustom size="sm" buttonType="icon" variant="tertiaryGray" onClick={handleCollapsed}>
            <SidebarSimple className="o-sidebar_iconCollapsed" size={20} />
          </ButtonCustom>
        </Tooltip>
      </Flex>
      <div className={mapModifiers('o-sidebar_wrapper', collapsed && 'collapsed')}>
        <Menu
          className="o-sidebar_menus"
          mode="inline"
          selectedKeys={selectedKeys}
          inlineCollapsed={collapsed}
          style={{ height: `calc(100% - ${(actionsRef?.current?.clientHeight || 0) + 24}px)`, width: '100%' } as React.CSSProperties}
        >
          {normalizedMenuItems.map((menuParent, index) => (
            <div key={`${index.toString()}-${menuParent.components || 'no-group'}`}>
              {!collapsed && menuParent.components && (
                <div className="o-sidebar_menus_title">{t(menuParent.components || '')}</div>
              )}

              {menuParent.items.map((menu) => (menu.items?.length
                ? renderMenuSub(menu)
                : (!menu.role || roles.includes(menu.role)) && (
                  <Menu.Item
                    key={menu.key}
                    title={<span>{menu.title}</span>}
                    className={mapModifiers('o-sidebar_menus_item', selectedKeys.includes(menu.key) && 'active')}
                  >
                    <Link
                      className={mapModifiers('o-sidebar_menus_collapsed', collapsed && 'on')}
                      to={menu.path || ''}
                      target={menu.target}
                      onClick={() => setSelectedKeys([menu.key])}
                    >
                      {selectedKeys.includes(menu.key) ? menu.icon?.active : menu.icon?.inactive}
                      {menu.title && !collapsed && (
                        <span className={mapModifiers('o-sidebar_menus_transition', !collapsed && 'on')}>
                          {menu.title}
                        </span>
                      )}
                    </Link>
                  </Menu.Item>
                )))}
            </div>
          ))}

        </Menu>
        <div ref={actionsRef} className={mapModifiers('o-sidebar_action', collapsed && 'collapsed')}>
          {/* <div className="o-sidebar_language">
          {!collapsed && t('system.language')}
          <Select
            defaultValue="vi"
            className="o-sidebar_languagePulldown"
            placement="topLeft"
            value={valueLanguage}
            onChange={changeLanguage}
          >
            <Option value="vi">
              <div className="o-sidebar_languagePulldown_option">
                <span role="img" aria-label="vi">
                  <Icon iconName="vietnam" size="20" />
                </span>
                {
                  !collapsed && t('system.languageVi')
                }
              </div>
            </Option>
            <Option value="en">
              <div className="o-sidebar_languagePulldown_option">
                <span role="img" aria-label="en">
                  <Icon iconName="english" size="20" />
                </span>
                {
                  !collapsed && t('system.languageEn')
                }
              </div>
            </Option>
          </Select>
        </div>
        {/* <div className="o-sidebar_theme">
          {
            !collapsed && 'Chế độ'
          }
          <Switch
            style={{ width: 64 }}
            checked={theme === 'dark'}
            onChange={changeTheme}
            checkedChildren="Tối"
            unCheckedChildren="Sáng"
          />
        </div> */}
          {/* <Divider /> */}
          <div style={{ width: '100%' }}>
            {/* {keyExist && (
              <div title="Settings" key="settings" className={mapModifiers('o-sidebar_menus_outside', tabParam === ('settings') && 'active', collapsed && 'collapsed')}>
                <Link
                  className={mapModifiers('fs-14x22 o-sidebar_menus_link o-sidebar_menus_collapsed o-sidebar_menus_textOutside', tabParam === ('settings') && 'active')}
                  onClick={() => setSelectedKeys(['settings'])}
                  to={`/edit-agent?key=${keyExist}&tab=settings&feat=${featParams}`}
                >
                  <Gear size={20} className={mapModifiers('o-sidebar_icon', tabParam?.includes('settings') && 'active')} />
                  {!collapsed && (
                    <span className={mapModifiers('o-sidebar_menus_transition', !collapsed && 'on')}>{t('Settings')}</span>
                  )}
                </Link>
              </div>
            )} */}
            {shouldShowMenuSetupGuide && (
              <div
                title="Setup Guide"
                className={mapModifiers('o-sidebar_setupGuide', collapsed && 'collapsed', isActiveSetupGuide && 'active')}
                onClick={() => navigate(`${ROUTE_PATHS.AGENT}?openSetupGuide=true`)}
              >
                <ListChecksIcon size={24} className="o-sidebar_setupGuide_icon" />
                {!collapsed && (
                  <span className="o-sidebar_setupGuide_text">
                    Setup Guide
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
